import React, { useState, useEffect } from 'react'

function getChatDraftKey(
    chatId: string,
    userId?: string,
): string | undefined {
    return userId ? `chatDraft-${chatId}-${userId}` : undefined
}

export function clearChatDraft(chatId: string, userId?: string): void {
    const draftKey = getChatDraftKey(chatId, userId)
    if (typeof window === 'undefined' || !draftKey) return

    try {
        window.localStorage.removeItem(draftKey)
    } catch (error) {
        console.error(`Failed to clear draft for key "${draftKey}":`, error)
    }
}

export function writeChatDraft(
    chatId: string,
    userId: string | undefined,
    text: string,
): void {
    const draftKey = getChatDraftKey(chatId, userId)
    if (typeof window === 'undefined' || !draftKey) return

    try {
        if (text) {
            window.localStorage.setItem(draftKey, JSON.stringify(text))
        } else {
            window.localStorage.removeItem(draftKey)
        }
    } catch (error) {
        console.error(`Failed to persist draft for key "${draftKey}":`, error)
    }
}

/**
 * A custom hook to manage and persist a chat message draft in localStorage.
 * The draft is scoped to a specific chat and user to prevent collisions.
 *
 * @param chatId The ID of the chat for which the draft is being stored.
 * @param userId The ID of the user who is writing the draft.
 * @param initialIfEmpty Optional. Seeds the draft with this value when there
 *                       is no existing draft in localStorage. Used by features
 *                       like OpenChatButton's `defaultText` to suggest text
 *                       without clobbering the user's existing draft.
 * @returns A stateful value for the draft and a function to update it.
 */
export function useChatDraft(
    chatId: string,
    userId?: string,
    initialIfEmpty?: string,
): [string, React.Dispatch<React.SetStateAction<string>>] {
    const [draft, setDraft] = useState('')

    /**
     * Key used for storing the unsent message draft in localStorage.
     * We prefix with `chatDraft` to clearly namespace these entries and
     * append the `chatId` and `userId` so each user maintains their own
     * draft for each chat thread.
     */
    const draftKey = getChatDraftKey(chatId, userId)

    // Load any existing draft when the component is mounted or when the
    // chat context changes (i.e., switching to a different chat).
    useEffect(() => {
        if (typeof window === 'undefined' || !draftKey) return

        try {
            const item = window.localStorage.getItem(draftKey)
            if (item) {
                const value: string = JSON.parse(item)
                setDraft(value)
            } else if (initialIfEmpty) {
                // No existing draft -- seed with the suggested initial text.
                setDraft(initialIfEmpty)
            } else {
                // Ensure we start with an empty string when there's no draft
                setDraft('')
            }
        } catch (error) {
            console.error(`Failed to read draft for key "${draftKey}":`, error)
        }
        // We intentionally only re-run this when the chat context changes,
        // not when `initialIfEmpty` changes -- otherwise typing/clearing
        // would risk re-seeding stale text.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftKey])

    // Persist draft whenever it changes. Remove the key entirely when the
    // draft is cleared (empty string) to avoid cluttering localStorage.
    useEffect(() => {
        if (typeof window === 'undefined' || !draftKey) return

        try {
            if (draft) {
                window.localStorage.setItem(draftKey, JSON.stringify(draft))
            } else {
                window.localStorage.removeItem(draftKey)
            }
        } catch (error) {
            console.error(
                `Failed to persist draft for key "${draftKey}":`,
                error,
            )
        }
    }, [draftKey, draft])

    return [draft, setDraft]
}
